import { useState, useEffect } from 'react';

// Importar todos los archivos de traducción
import en from '../translations/en.json';
import es from '../translations/es.json';
import bg from '../translations/bg.json';
import cs from '../translations/cs.json';
import fi from '../translations/fi.json';
import el from '../translations/el.json';

// Mapeo de idiomas a sus traducciones
const translations = {
  en,
  es,
  bg,
  cs,
  fi,
  el
};

/**
 * Hook personalizado para manejar traducciones
 * @returns {Object} { t, language, setLanguage }
 * - t: función para obtener traducciones
 * - language: idioma actual
 * - setLanguage: función para cambiar el idioma
 */
const useTranslation = () => {
  const [language, setLanguageState] = useState(() => {
    // Leer el idioma guardado en localStorage
    const saved = localStorage.getItem('greenscape_language');
    return saved ? JSON.parse(saved) : 'en'; // Por defecto inglés
  });

  const [currentTranslations, setCurrentTranslations] = useState(translations[language] || translations.en);

  useEffect(() => {
    // Función para verificar cambios en localStorage
    const checkLanguageChange = () => {
      const saved = localStorage.getItem('greenscape_language');
      const newLanguage = saved ? JSON.parse(saved) : 'en';
      if (newLanguage !== language) {
        setLanguageState(newLanguage);
      }
    };

    // Verificar cambios cada vez que la ventana recibe foco
    window.addEventListener('storage', checkLanguageChange);
    
    // También verificar periódicamente (cada segundo)
    const interval = setInterval(checkLanguageChange, 1000);

    return () => {
      window.removeEventListener('storage', checkLanguageChange);
      clearInterval(interval);
    };
  }, [language]);

  useEffect(() => {
    // Actualizar las traducciones cuando cambia el idioma
    setCurrentTranslations(translations[language] || translations.en);
  }, [language]);

  /**
   * Función para obtener una traducción por su clave
   * @param {string} key - Clave de la traducción (ej: "landing.startButton")
   * @param {Object} params - Parámetros para reemplazar en el texto (ej: {current: 1, total: 5})
   * @returns {string} Texto traducido
   * 
   * Ejemplos:
   * t('landing.startButton') => "START"
   * t('playerDataForm.playerProgress', {current: 1, total: 5}) => "Player 1 of 5"
   * t('awarenessGame.questions.0.title') => "QUESTION 1"
   */
  const t = (key, params = {}) => {
    try {
      // Dividir la clave por puntos para navegar el objeto
      const keys = key.split('.');
      let value = currentTranslations;

      // Navegar por el objeto de traducciones
      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k];
        } else {
          // Si no se encuentra la clave, retornar la clave misma como fallback
          console.warn(`Translation key not found: ${key}`);
          return key;
        }
      }

      // Si el valor es undefined o null, retornar la clave
      if (value === undefined || value === null || value === '') {
        console.warn(`Translation value is empty for key: ${key}`);
        // Intentar con inglés como fallback
        const englishValue = getEnglishFallback(key);
        return englishValue || key;
      }

      // Si hay parámetros, reemplazarlos en el texto
      if (Object.keys(params).length > 0 && typeof value === 'string') {
        return replaceParams(value, params);
      }

      return value;
    } catch (error) {
      console.error(`Error getting translation for key: ${key}`, error);
      return key;
    }
  };

  /**
   * Función auxiliar para obtener el valor en inglés como fallback
   */
  const getEnglishFallback = (key) => {
    try {
      const keys = key.split('.');
      let value = translations.en;
      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k];
        } else {
          return null;
        }
      }
      return value;
    } catch {
      return null;
    }
  };

  /**
   * Función auxiliar para reemplazar parámetros en el texto
   * Ejemplo: "Player {current} of {total}" con {current: 1, total: 5} => "Player 1 of 5"
   */
  const replaceParams = (text, params) => {
    let result = text;
    Object.keys(params).forEach(key => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      result = result.replace(regex, params[key]);
    });
    return result;
  };

  /**
   * Función para cambiar el idioma
   * (normalmente no se usa directamente, se usa setSelectedLanguage de useGameState)
   */
  const setLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguageState(newLanguage);
      localStorage.setItem('greenscape_language', JSON.stringify(newLanguage));
    } else {
      console.error(`Language not supported: ${newLanguage}`);
    }
  };

  return {
    t,           // Función de traducción
    language,    // Idioma actual
    setLanguage  // Función para cambiar idioma
  };
};

export default useTranslation;

